/**
 * The action a transcript page runs by itself, carried on the URL (SEO_BRIEF_09 Part C).
 *
 * /youtube-summarizer and /video-summarizer send the visitor on to `/t/{id}?do=summarize`
 * (`&lang=xx` for a summary in another language), and /caption-generator to
 * `/t/{id}?do=captions&platform=tiktok`. While the job runs that id is the job's, and the job
 * page (pages/job.ts) carries the same query on to the transcript's own link when it moves there,
 * as it carries `?download=` (lib/download-pref.ts). The transcript page runs the action once and
 * takes the query off its address, so a reload or a shared link does not run it again.
 *
 * `?do=translate&lang=es` is the server's own (contracts/api.md §3.2): a link page asked for a
 * translation of a video whose captions are in another language, and the captions were served as
 * they are, for the transcript page to translate (a track too long to wait for, or a language
 * model busy or out just then). The answer's `transcript_url` carries it; `lang` is required.
 *
 * This side refuses anything that is not one of the actions, a platform on the caption list or a
 * language code, so a hand-edited URL cannot smuggle anything through the job page.
 */

import { CAPTION_PLATFORMS, type CaptionPlatform, TARGET_CODE } from './usage.ts';

export const ACTION_PARAM = 'do';
export const PLATFORM_PARAM = 'platform';
export const LANG_PARAM = 'lang';
const PARAMS = [ACTION_PARAM, PLATFORM_PARAM, LANG_PARAM] as const;

export type AutoAction =
  | { readonly kind: 'summarize'; readonly lang: string | null }
  | { readonly kind: 'captions'; readonly platform: CaptionPlatform; readonly lang: string | null }
  | { readonly kind: 'translate'; readonly lang: string };

const RELATIVE = 'http://relative.invalid';

function isPlatform(value: string | null): value is CaptionPlatform {
  return value !== null && (CAPTION_PLATFORMS as readonly string[]).includes(value);
}

/** The action a query string asks for, or null for none (or one that is not on the lists). */
export function autoActionFrom(search: string): AutoAction | null {
  const query = new URLSearchParams(search);
  const raw = query.get(LANG_PARAM);
  const lang = raw !== null && TARGET_CODE.test(raw) ? raw : null;
  switch (query.get(ACTION_PARAM)) {
    case 'summarize':
      return { kind: 'summarize', lang };
    case 'captions': {
      const platform = query.get(PLATFORM_PARAM);
      return isPlatform(platform) ? { kind: 'captions', platform, lang } : null;
    }
    case 'translate':
      return lang !== null ? { kind: 'translate', lang } : null;
    default:
      return null;
  }
}

/** A path stays a path; anything else keeps its origin. */
function serialise(parsed: URL): string {
  return parsed.origin === RELATIVE
    ? `${parsed.pathname}${parsed.search}${parsed.hash}`
    : parsed.href;
}

/**
 * `url` with `action` set on it: `?do=`, `&platform=` for captions and `&lang=` when it has one
 * (a translation always has one).
 * The one writer of these parameters: the job page (below) and the tool pages' hand-off
 * (lib/hand-off.ts `withThen`) both go through it, so what they write is what
 * `autoActionFrom` reads.
 */
export function withAction(url: string, action: AutoAction): string {
  const parsed = new URL(url, RELATIVE);
  parsed.searchParams.set(ACTION_PARAM, action.kind);
  if (action.kind === 'captions') parsed.searchParams.set(PLATFORM_PARAM, action.platform);
  else parsed.searchParams.delete(PLATFORM_PARAM);
  if (action.lang !== null) parsed.searchParams.set(LANG_PARAM, action.lang);
  else parsed.searchParams.delete(LANG_PARAM);
  return serialise(parsed);
}

/** `url` with the action `search` asks for set on it, or `url` untouched when it asks for none. */
export function withAutoAction(url: string, search: string): string {
  const action = autoActionFrom(search);
  return action === null ? url : withAction(url, action);
}

/** `url` with the action's parameters taken off, everything else as it was. */
export function withoutAutoAction(url: string): string {
  const parsed = new URL(url, RELATIVE);
  for (const name of PARAMS) parsed.searchParams.delete(name);
  return serialise(parsed);
}
